import React from "react";
import { motion, AnimatePresence } from "framer-motion";

interface EnhancedSpotlightOverlayProps {
  targetRect: DOMRect | null;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  spotlightOpacity?: number;
  spotlightPadding?: number;
  children?: React.ReactNode;
}

export const EnhancedSpotlightOverlay: React.FC<EnhancedSpotlightOverlayProps> = ({
  targetRect,
  onClick,
  spotlightOpacity = 0.1,
  spotlightPadding = 8,
  children
}) => {
  // Om ingen targetRect finns, visa hela skärmen som overlay
  const isFullScreenOverlay = !targetRect || 
    targetRect.width === 0 || 
    targetRect.height === 0 || 
    (document.body.getBoundingClientRect().width === targetRect?.width &&
     document.body.getBoundingClientRect().height === targetRect?.height);

  if (isFullScreenOverlay) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed inset-0 bg-black/70 z-[100] tutorial-overlay no-blur" // Tar bort blur-effekten
        onClick={onClick}
      >
        {children}
      </motion.div>
    );
  }

  // LÖSNING: Använd CSS clip-path för att göra ett "hål" i overlayen istället för att försöka 
  // lägga ett element ovanpå (vilket skapar z-index-problem)
  const padding = Math.max(8, spotlightPadding);
  const adjustedPadding = Math.min(padding, Math.min(targetRect.width, targetRect.height) / 2);
  
  // Beräkna position och storlek för hålet i överläggningen
  const holeLeft = targetRect.left - adjustedPadding;
  const holeTop = targetRect.top - adjustedPadding;
  const holeWidth = targetRect.width + adjustedPadding * 2;
  const holeHeight = targetRect.height + adjustedPadding * 2;
  
  // Beräkna clip-path för ett rektangulärt hål
  // format: polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0, [hål koordinater])
  const clipPath = `polygon(
    0% 0%, 
    100% 0%, 
    100% 100%, 
    0% 100%,
    0% 0%,
    ${holeLeft}px ${holeTop}px, 
    ${holeLeft}px ${holeTop + holeHeight}px, 
    ${holeLeft + holeWidth}px ${holeTop + holeHeight}px, 
    ${holeLeft + holeWidth}px ${holeTop}px, 
    ${holeLeft}px ${holeTop}px
  )`;

  return (
    <>
      {/* Huvudöverläggning med clip-path för hålet */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed inset-0 z-[100] pointer-events-auto bg-black/85 no-blur"
        style={{ 
          clipPath: clipPath,
          WebkitClipPath: clipPath
        }}
        onClick={onClick}
      />
      
      {/* Highlightad ram runt målområdet */}
      <div
        className="fixed z-[101] pointer-events-none"
        style={{
          top: holeTop,
          left: holeLeft,
          width: holeWidth,
          height: holeHeight,
          boxShadow: "0 0 0 4px rgba(249, 115, 22, 0.7), 0 0 20px rgba(249, 115, 22, 0.5)",
          borderRadius: "8px",
          backgroundColor: `rgba(249, 115, 22, ${Math.max(0.07, spotlightOpacity)})`,
          animation: "improved-spotlight-pulse 3s infinite"
        }}
      />
      
      {children}
    </>
  );
};

export default EnhancedSpotlightOverlay;